type BrowserSpeechRecognitionResultList = {
  length: number;
  [index: number]: {
    isFinal: boolean;
    0?: { transcript?: string };
  };
};

export type BrowserSpeechRecognitionResultEvent = {
  resultIndex: number;
  results: BrowserSpeechRecognitionResultList;
};

export type BrowserSpeechRecognitionInstance = {
  continuous: boolean;
  interimResults: boolean;
  lang: string;
  onresult: ((event: BrowserSpeechRecognitionResultEvent) => void) | null;
  onerror: ((event: { error: string }) => void) | null;
  onend: (() => void) | null;
  start: () => void;
  stop: () => void;
};

type BrowserSpeechRecognitionConstructor = new() => BrowserSpeechRecognitionInstance;

/** Appends a finalized browser speech chunk to accumulated transcript text. */
export const appendFinalSpeechResult = (accumulated: string, finalTranscript: string): string => {
  const trimmed = finalTranscript.trim();
  if (!trimmed) return accumulated;
  if (!accumulated.trim()) return trimmed;
  return `${accumulated.trim()} ${trimmed}`;
};

/** Combines finalized and interim speech for the live textarea display. */
export const mergeSpeechDisplayText = (finalText: string, interimText: string): string => {
  const finals = finalText.trim();
  const interim = interimText.trim();
  if (!interim) return finals;
  if (!finals) return interim;
  return `${finals} ${interim}`;
};

/** Parses a SpeechRecognition result event into accumulated finals, interim, and new utterances. */
export const parseSpeechRecognitionResultEvent = (
  event: BrowserSpeechRecognitionResultEvent,
  accumulatedFinalText: string,
): { finalText: string; interimText: string; newFinalUtterances: string[] } => {
  let finalText = accumulatedFinalText;
  let interimText = '';
  const newFinalUtterances: string[] = [];

  for (let index = event.resultIndex; index < event.results.length; index++) {
    const result = event.results[index];
    const transcript = result[0]?.transcript ?? '';
    if (result.isFinal) {
      const trimmed = transcript.trim();
      if (trimmed) newFinalUtterances.push(trimmed);
      finalText = appendFinalSpeechResult(finalText, transcript);
    } else {
      interimText += transcript;
    }
  }

  return { finalText, interimText, newFinalUtterances };
};

export const getBrowserSpeechRecognitionConstructor = (): BrowserSpeechRecognitionConstructor | undefined => {
  if (typeof window === 'undefined') return undefined;
  const browserWindow = window as Window & {
    SpeechRecognition?: BrowserSpeechRecognitionConstructor;
    webkitSpeechRecognition?: BrowserSpeechRecognitionConstructor;
  };
  return browserWindow.SpeechRecognition ?? browserWindow.webkitSpeechRecognition;
};

/** Chrome often ends continuous sessions after a pause or mic handoff — restart while recording. */
export const shouldRestartSpeechRecognition = (
  errorCode: string,
  isRecording: boolean,
): boolean =>
  isRecording
  && (errorCode === 'no-speech' || errorCode === 'network' || errorCode === 'aborted' || errorCode === 'audio-capture');
